/**
 * What `LinesGrid` decides without a page to draw on: where Enter and Tab move to, and what a block
 * pasted from a spreadsheet writes into the lines. Pure, so each is tested on its own (lines.test.tsx).
 */
import { normalise, readDay } from "./fields.js";
/**
 * Where Enter moves focus from a cell: the next editable cell of the line, then the first of the
 * next line, and from the last cell of the last line a new line (`add`, which the grid refuses at
 * its `max`). Tab is left to the browser — the order of the page, remove buttons included — so a
 * reader can predict it (Fable, 2026-10-27). Null: nowhere to go.
 */
export function nextCell(at, lines, editable) {
    if (editable <= 0 || at.line < 0 || at.line >= lines || at.column < 0 || at.column >= editable)
        return null;
    if (at.column < editable - 1)
        return { line: at.line, column: at.column + 1 };
    return at.line === lines - 1 ? 'add' : { line: at.line + 1, column: 0 };
}
/**
 * A block copied from a spreadsheet, as rows of cells: tabs between cells, a line break between
 * rows, a quoted cell may hold either (`"a ""b"""`). One trailing line break — every spreadsheet
 * adds it — is not an empty row. Null for plain text: one value with neither, pasted as the
 * browser pastes it.
 */
export function readBlock(text) {
    const t = text.replace(/\r\n?/g, '\n').replace(/\n$/, '');
    if (!/[\t\n]/.test(t))
        return null;
    const rows = [];
    let row = [];
    let cell = '';
    let quoted = false;
    for (let i = 0; i < t.length; i++) {
        const c = t[i];
        if (quoted) {
            if (c === '"' && t[i + 1] === '"') {
                cell += '"';
                i++;
            }
            else if (c === '"')
                quoted = false;
            else
                cell += c;
        }
        else if (c === '"' && cell === '')
            quoted = true;
        else if (c === '\t') {
            row.push(cell);
            cell = '';
        }
        else if (c === '\n') {
            row.push(cell);
            rows.push(row);
            row = [];
            cell = '';
        }
        else
            cell += c;
    }
    row.push(cell);
    rows.push(row);
    return rows;
}
const YES = /^(true|yes|y|1|x|✓|✔|نعم|صح)$/i;
const NO = /^(false|no|n|0|لا|خطأ)$/i;
/**
 * One pasted cell as the value its column holds. `ok: false` when the text says nothing the column
 * can hold — a word in a figure, a day not on the calendar, a choice not offered: the cell is left
 * empty and counted, so the person is told rather than handed a value nobody typed. An empty cell
 * empties the cell (a box stays unticked).
 */
export function readCell(column, text, order) {
    const t = text.trim();
    if (column.type === 'text')
        return { ok: true, value: t };
    if (column.type === 'boolean') {
        if (t === '' || NO.test(t))
            return { ok: true, value: false };
        return YES.test(t) ? { ok: true, value: true } : { ok: false, value: false };
    }
    if (t === '')
        return { ok: true, value: null };
    if (column.type === 'number') {
        // A figure copied with its currency or a percent sign keeps only the figure; anything else is refused.
        const figure = normalise(t.replace(/^[^\d٠-٩۰-۹+\-.,٫]+|[^\d٠-٩۰-۹.,٫]+$/g, ''));
        const n = Number(figure);
        return figure !== '' && Number.isFinite(n) ? { ok: true, value: n } : { ok: false, value: null };
    }
    if (column.type === 'date') {
        const iso = readDay(t, order);
        return iso ? { ok: true, value: iso } : { ok: false, value: null };
    }
    // A choice: its value as written, else its words, letter case aside.
    const options = column.options ?? [];
    const byValue = options.find((o) => o.value !== null && o.value !== undefined && String(o.value) === t);
    const lower = t.toLocaleLowerCase();
    const found = byValue ?? options.find((o) => o.label.trim().toLocaleLowerCase() === lower);
    return found ? { ok: true, value: found.value } : { ok: false, value: null };
}
/**
 * A block written into the lines from a cell rightward over the editable columns and downward,
 * adding lines as it needs them (never past `max`). Cells past the last editable column are
 * dropped; a computed column is never written, because it is not among `columns`. The rows are
 * new objects; the ones the block does not reach are handed back as they were.
 */
export function pasteBlock(columns, rows, block, at, blank, opts) {
    const out = [...rows];
    let lines = 0;
    let unread = 0;
    let dropped = 0;
    block.forEach((cells, i) => {
        const line = at.line + i;
        if (line >= out.length) {
            if (opts.max !== undefined && out.length >= opts.max) {
                dropped++;
                return;
            }
            out.push(blank());
        }
        const next = { ...out[line] };
        let wrote = false;
        cells.forEach((text, j) => {
            const column = columns[at.column + j];
            if (!column)
                return;
            const read = readCell(column, text, opts.order);
            if (!read.ok)
                unread++;
            next[column.key] = read.value;
            wrote = true;
        });
        out[line] = next;
        if (wrote)
            lines++;
    });
    return { rows: out, lines, unread, dropped };
}
//# sourceMappingURL=lines-grid.js.map